import React, { useMemo, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import useABTest from '../../hooks/useABTest';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';
import useSalesFunnelViewportGuard from './useSalesFunnelViewportGuard';
import './SalesFunnelViewportGuard.css';
import './SalesChoosePage.css';

// Copy variants — tight and action-focused. Rob (Apr 9): "I don't want to read anything".
// Compliance note: "Two ways to get rich" was promissory language (FINRA Rule 2210), reworked
// to outcome-neutral. Source: https://www.finra.org/rules-guidance/notices/20-21
const COPY_VARIANTS = {
  control: {
    heading: 'Pick your path.',
    marketplace: {
      tag: 'Fastest',
      emoji: '🚀',
      title: 'Copy a proven bot',
      cta: 'Browse now',
    },
    prompt: {
      tag: 'Custom',
      emoji: '🧠',
      title: 'Build your own',
      cta: 'Start building',
    },
  },
  benefit_focus: {
    heading: 'How do you want to trade?',
    marketplace: {
      tag: 'Fastest',
      emoji: '⚡',
      title: 'Copy a winner',
      cta: 'Browse bots',
    },
    prompt: {
      tag: 'Custom',
      emoji: '✨',
      title: 'Create from scratch',
      cta: 'Start building',
    },
  },
};

export default function SalesChoosePage() {
  const location = useLocation();
  const navigate = useNavigate();
  useSalesFunnelViewportGuard();

  const experiment = ACTIVE_EXPERIMENTS.SALES_CHOOSE_COPY;
  const { variant: copyVariant } = useABTest(
    experiment.name,
    experiment.featureFlag,
    'control'
  );

  // Randomize button order per visit — stable for the session
  const [marketplaceFirst] = useState(() => Math.random() < 0.5);

  // Forward all query params to next page
  const queryString = useMemo(() => location.search, [location.search]);

  // Canonical PAGE_VIEW — fire ONCE on mount (forwarded to GA4/GTM/FB Pixel).
  // Do not include A/B variant in the dep array or it re-fires when the flag resolves.
  useEffect(() => {
    pushEvent({
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page_path: '/sales-choose',
      page_title: 'BotSpot - Pick Your Path',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.getElementById('sales-choose-boot')?.remove();
    document.documentElement.classList.remove('sales-choose-boot');
    document.documentElement.classList.add('sales-choose-root');
    document.body.classList.add('sales-choose-body');
    return () => {
      document.documentElement.classList.remove('sales-choose-boot');
      document.documentElement.classList.remove('sales-choose-root');
      document.body.classList.remove('sales-choose-body');
    };
  }, []);

  // Custom sales_choose_page_view — fires once per (variant, order) pair so that if
  // the A/B flag resolves late, we capture the experiment exposure with correct context.
  useEffect(() => {
    pushEvent({
      event: 'sales_choose_page_view',
      copy_variant: copyVariant,
      marketplace_first: marketplaceFirst,
    });
  }, [copyVariant, marketplaceFirst]);

  const copy = COPY_VARIANTS[copyVariant] || COPY_VARIANTS.control;

  // Build comprehensive telemetry payload — Rob (Apr 9): this data is priceless,
  // capture everything about the exact choice the user saw at decision time.
  // Goal: understand what copy/imagery drives each path, so we can eventually
  // infer user intent without asking.
  const buildSelectionPayload = (pathKey, chosenCopy, otherCopy, buttonPosition) => {
    const params = new URLSearchParams(location.search);
    return {
      path: pathKey, // 'marketplace' | 'prompt'
      selected_path: pathKey,
      copy_variant: copyVariant,
      button_position: buttonPosition, // 'left' | 'right'

      // Full snapshot of the chosen card
      chosen_heading: copy.heading,
      chosen_tag: chosenCopy.tag,
      chosen_emoji: chosenCopy.emoji,
      chosen_title: chosenCopy.title,
      chosen_cta: chosenCopy.cta,

      // Full snapshot of the card they DID NOT pick (for diff analysis)
      rejected_tag: otherCopy.tag,
      rejected_emoji: otherCopy.emoji,
      rejected_title: otherCopy.title,
      rejected_cta: otherCopy.cta,

      // Context for downstream funnel analysis
      heading: copy.heading,
      randomized_order: marketplaceFirst ? 'marketplace_first' : 'prompt_first',
      page_url: `${window.location.origin}${location.pathname}${location.search}`,
      referrer: document.referrer || null,
      lead_id: params.get('leadId') || null,
      lead_email: params.get('email') || null,
      sales_page_signup_framing_variant: params.get('sales_page_signup_framing_variant') || null,
      sales_page_form_variant: params.get('sales_page_form_variant') || null,
      sales_page_framing_variant: params.get('sales_page_framing_variant') || null,
      sales_page_design_variant: params.get('sales_page_design_variant') || null,
      utm_source: params.get('utm_source') || null,
      utm_campaign: params.get('utm_campaign') || null,
      timestamp_iso: new Date().toISOString(),
    };
  };

  const handleMarketplace = () => {
    const payload = buildSelectionPayload(
      'marketplace',
      copy.marketplace,
      copy.prompt,
      marketplaceFirst ? 'left' : 'right'
    );
    pushEvent({ event: 'sales_choose_path_selected', ...payload });
    navigate(`/sales-marketplace${queryString}`);
  };

  const handlePrompt = () => {
    const payload = buildSelectionPayload(
      'prompt',
      copy.prompt,
      copy.marketplace,
      marketplaceFirst ? 'right' : 'left'
    );
    pushEvent({ event: 'sales_choose_path_selected', ...payload });
    navigate(`/sales-ai-prompt${queryString}`);
  };

  const marketplaceCard = (
    <button
      key="marketplace"
      className="sales-choose-card sales-choose-card--marketplace"
      onClick={handleMarketplace}
    >
      <div className="sales-choose-card-content">
        <div className="sales-choose-card-top">
          <div className="sales-choose-card-tag">{copy.marketplace.tag}</div>
        </div>
        <h2 className="sales-choose-card-title">{copy.marketplace.title}</h2>
        <div className="sales-choose-card-cta">
          <span className="sales-choose-cta-text">{copy.marketplace.cta}</span>
          <span className="sales-choose-arrow">→</span>
        </div>
      </div>
    </button>
  );

  const promptCard = (
    <button
      key="prompt"
      className="sales-choose-card sales-choose-card--prompt"
      onClick={handlePrompt}
    >
      <div className="sales-choose-card-content">
        <div className="sales-choose-card-top">
          <div className="sales-choose-card-tag">{copy.prompt.tag}</div>
        </div>
        <h2 className="sales-choose-card-title">{copy.prompt.title}</h2>
        <div className="sales-choose-card-cta">
          <span className="sales-choose-cta-text">{copy.prompt.cta}</span>
          <span className="sales-choose-arrow">→</span>
        </div>
      </div>
    </button>
  );

  const cards = marketplaceFirst
    ? [marketplaceCard, promptCard]
    : [promptCard, marketplaceCard];

  return (
    <>
      <Helmet>
        <title>BotSpot — Pick Your Path to Automated Trading 🚀</title>
        <meta name="description" content="Pick a winning trading strategy or build your own with AI. Start automated trading in minutes." />
      </Helmet>

      <div className="sales-choose-page">
        <div className="sales-choose-content">
          <div className="sales-choose-hero">
            <h1 className="sales-choose-heading">
              <span className="sales-choose-heading-line">{copy.heading}</span>
            </h1>
          </div>

          <div className="sales-choose-cards">{cards}</div>

          <p className="sales-choose-disclaimer">
            Past performance does not guarantee future results. Automated trading involves risk of loss.
          </p>
        </div>
      </div>
    </>
  );
}
